import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isRole, type Role } from "../roles.js";
import { renewWithFirebase, signOutOfFirebase } from "./firebase.js";

/**
 * Client session (E1.8). The authenticated principal carries subject + tenant + role + a short-lived
 * token; the API client sends the token, and every route under <RequireAuth> needs a live session —
 * an unauthenticated visit redirects to /login (E8.2-AC1). The role drives nav + projection (AC2).
 */
export interface Session {
  readonly subject: string;
  readonly tenant: string;
  readonly role: Role;
  readonly token: string;
}

interface SessionContextValue {
  readonly session: Session | null;
  readonly signIn: (session: Session) => void;
  /** Ends the session and the sign-in behind it, so nothing can renew it. */
  readonly signOut: () => void;
  /**
   * NX-3433: call on a 401, passing the token the refused request carried. Renews the session once
   * (no reload, no /login); signs out only when renewal fails. Resolves the token to retry with,
   * else null. After a sign-out it never renews, and a 401 for an older token than the current
   * session's resolves the current token without renewing.
   */
  readonly onUnauthenticated: (refusedToken?: string) => Promise<string | null>;
}

/** Renew this long before the session token expires. */
const RENEW_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
/** Two renewals inside this window mean renewal is not helping: sign out instead of looping. */
const MIN_RENEW_INTERVAL_MS = 60 * 1000;

/** The `exp` (seconds) a session token carries in its first segment, or null when unreadable. */
export function sessionTokenExpiry(token: string): number | null {
  const body = token.split(".")[0];
  if (!body) return null;
  try {
    const json = atob(body.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/** The default renewal: re-exchange the Firebase user's refreshed ID token for a session. */
async function renewFromFirebase(): Promise<Session | null> {
  const renewed = await renewWithFirebase();
  if (!renewed || !isRole(renewed.role)) return null;
  return {
    subject: renewed.subject,
    tenant: renewed.tenant,
    role: renewed.role,
    token: renewed.token,
  };
}

const SessionContext = createContext<SessionContextValue | null>(null);
const STORAGE_KEY = "senesight.session";

function loadStored(): Session | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (parsed.subject && parsed.tenant && parsed.token && isRole(parsed.role)) {
      return {
        subject: parsed.subject,
        tenant: parsed.tenant,
        role: parsed.role,
        token: parsed.token,
      };
    }
  } catch {
    /* fall through to no session */
  }
  return null;
}

export function SessionProvider({
  children,
  initialSession,
  renew = renewFromFirebase,
  endSignIn = signOutOfFirebase,
  now = Date.now,
}: {
  children: ReactNode;
  initialSession?: Session | null;
  /** Injected for tests; production renews from the Firebase sign-in. */
  renew?: () => Promise<Session | null>;
  endSignIn?: () => Promise<void>;
  now?: () => number;
}): ReactNode {
  const [session, setSession] = useState<Session | null>(
    initialSession !== undefined ? initialSession : loadStored(),
  );

  // Every explicit sign-in or sign-out starts a new generation. A renewal that began in an older
  // generation never applies its result, so a sign-out is final even while an exchange is in flight.
  const generation = useRef(0);
  // One renewal at a time: every 401 that arrives while one is running waits for it.
  const inflight = useRef<Promise<string | null> | null>(null);
  // The live session token, readable from callbacks without re-creating them; null once ended.
  const liveToken = useRef<string | null>(session?.token ?? null);
  // Every token of the CURRENT sign-in (its first token and each renewal of it). A refused token
  // outside this set belongs to an earlier sign-in, possibly another account: never adopted.
  const signInTokens = useRef<Set<string>>(new Set(session ? [session.token] : []));
  const liveIdentity = useRef<{ subject: string; tenant: string } | null>(
    session ? { subject: session.subject, tenant: session.tenant } : null,
  );

  const applySession = useCallback((next: Session) => {
    liveToken.current = next.token;
    signInTokens.current.add(next.token);
    liveIdentity.current = { subject: next.subject, tenant: next.tenant };
    setSession(next);
    try {
      globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — session is still held in memory for this tab */
    }
  }, []);

  const endSession = useCallback(() => {
    generation.current += 1;
    inflight.current = null;
    liveToken.current = null;
    signInTokens.current = new Set();
    liveIdentity.current = null;
    setSession(null);
    try {
      globalThis.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    void endSignIn();
  }, [endSignIn]);

  const signIn = useCallback(
    (next: Session) => {
      generation.current += 1;
      inflight.current = null;
      signInTokens.current = new Set();
      applySession(next);
    },
    [applySession],
  );

  const signOut = endSession;

  const lastRenewAt = useRef<number | null>(null);
  const onUnauthenticated = useCallback(
    (refusedToken?: string): Promise<string | null> => {
      // Signed out: a late 401 from a request sent before the sign-out never restores a session.
      if (liveToken.current === null) return Promise.resolve(null);
      // A 401 for a token the session has already replaced. Renewed in place (same sign-in):
      // retry with the current token. Refused under an earlier sign-in: abort, so a stale action
      // never runs as the next account.
      if (refusedToken !== undefined && refusedToken !== liveToken.current) {
        return Promise.resolve(signInTokens.current.has(refusedToken) ? liveToken.current : null);
      }
      if (inflight.current) return inflight.current;
      const at = now();
      if (lastRenewAt.current !== null && at - lastRenewAt.current < MIN_RENEW_INTERVAL_MS) {
        // A fresh session was refused too: renewal cannot fix this, so end it.
        endSession();
        return Promise.resolve(null);
      }
      lastRenewAt.current = at;
      const started = generation.current;
      const current: Promise<string | null> = renew()
        .then((next) => {
          if (generation.current !== started) return null; // signed out (or in) meanwhile
          const identity = liveIdentity.current;
          if (
            !next ||
            (identity !== null &&
              (next.subject !== identity.subject || next.tenant !== identity.tenant))
          ) {
            // No renewal, or one for a different account: renewal never switches identity.
            endSession();
            return null;
          }
          applySession(next);
          return next.token;
        })
        .catch(() => {
          if (generation.current === started) endSession();
          return null;
        })
        .finally(() => {
          if (inflight.current === current) inflight.current = null;
        });
      inflight.current = current;
      return current;
    },
    [now, renew, applySession, endSession],
  );

  // Renew shortly before the token expires, so an idle tab never meets a 401 at all.
  const token = session?.token ?? null;
  useEffect(() => {
    if (!token) return;
    const exp = sessionTokenExpiry(token);
    if (exp === null) return;
    const delay = Math.max(0, exp * 1000 - RENEW_BEFORE_EXPIRY_MS - now());
    const timer = setTimeout(() => {
      void onUnauthenticated();
    }, delay);
    return () => clearTimeout(timer);
  }, [token, now, onUnauthenticated]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, signIn, signOut, onUnauthenticated }),
    [session, signIn, signOut, onUnauthenticated],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within a <SessionProvider>");
  return ctx;
}

/** Route guard: a live session is required, else redirect to /login keeping the intended path (AC1). */
export function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { session } = useSession();
  const location = useLocation();
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}
